// CUSTOM: personalised-review-renderer.js
// Review page rendering and content module.
// Handles all UI-state and content-rendering concerns for the personalised review page:
//   renderBundleItems, updateUIForMode, surfaceSubscriptionStateSignal,
//   updatePersonalisedHeading, updateReviewIntroQuiz.
// Depends on: personalised-config.js, personalised-utils.js, personalised-bundle-ui.js,
//   personalised-storage-manager.js.

(function() {
  window.personalised = window.personalised || {};

  const reviewRenderer = (function() {

    // =============================================================================
    // ===== RENDER =====
    // =============================================================================

    /**
     * [SHARED] Renders bundle items on the review page.
     * Uses canonical rendering method if available, otherwise displays a fallback message.
     * @returns {boolean} - Returns false if rendering wasn't possible, true if successful
     */
    function renderBundleItems() {
      const config = window.personalised.config;
      window.personalised?.debug?.trace('review:renderBundleItems', {});
      if (window.personalised.bundleUI && typeof window.personalised.bundleUI.renderBundleItems === 'function') {
        return window.personalised.bundleUI.renderBundleItems();
      } else {
        if (window.personalised?.debugMode) console.warn('No canonical renderBundleItems found, rendering might not be optimal');
        const listEl = document.getElementById('bundle-items');
        if (!listEl) {
          if (window.personalised?.debugMode) console.warn('Bundle items element not found (#bundle-items)');
          return false;
        }
        // Basic rendering fallback - should never be used in production
        listEl.innerHTML = '<p>' + config.uiLabels.bundleLoadingError + '</p>';
        return false;
      }
    }

    // =============================================================================
    // ===== UI STATE =====
    // =============================================================================

    /**
     * [SHARED] PR-1.2: Surface inactive-subscription state as data attributes on the review wrapper.
     * Sets data-has-inactive-subscription and data-subscription-status for a cancelled-only
     * returning customer. Clears those attributes for all other customer types.
     * Does NOT add any visible UI — attributes only.
     */
    function surfaceSubscriptionStateSignal() {
      const wrapper = document.querySelector('#personalised-review-wrapper');
      if (!wrapper) return;

      const storageManager = window.personalised?.storageManager;
      const hasInactive = storageManager
        ? storageManager.hasInactiveSubscription()
        : false;

      if (hasInactive) {
        const status = (storageManager?.getMetadata('inactiveSubscriptionStatus') || 'cancelled');
        wrapper.setAttribute('data-has-inactive-subscription', 'true');
        wrapper.setAttribute('data-subscription-status', status);
        window.personalised?.debug?.trace('review:inactiveSubscriptionSignalSurfaced', {
          inactiveSubscriptionId: storageManager?.getMetadata('inactiveSubscriptionId'),
          inactiveSubscriptionStatus: status
        });
      } else {
        wrapper.removeAttribute('data-has-inactive-subscription');
        wrapper.removeAttribute('data-subscription-status');
      }
    }

    /**
     * [SHARED] Updates UI elements based on current mode.
     * Sets data-mode on the review wrapper, refreshes the subscription-state signal,
     * and updates debug info attribute.
     */
    function updateUIForMode() {
      const mode = window.personalised.utils.getMode();

      // Update wrapper data attribute
      const wrapper = document.querySelector('#personalised-review-wrapper');
      if (wrapper) {
        wrapper.setAttribute('data-mode', mode);
        // Add debugging attribute for visual verification
        wrapper.setAttribute('data-debug-mode', mode || 'unknown');
      }
      // PR-1.2: keep subscription state signal current whenever UI is refreshed
      surfaceSubscriptionStateSignal();

      // Log detailed debug info to console
      if (window.personalised?.debugMode) console.debug('[Checkout Button Debug]', {
        mode: mode,
        viewport: window.innerWidth > 1200 ? 'desktop' : 'mobile',
        desktopButtonVisible: document.getElementById('checkout-button-desktop-wrapper')?.style.display !== 'none',
        mobileButtonVisible: document.getElementById('checkout-button-mobile-wrapper')?.style.display !== 'none'
      });
    }

    // =============================================================================
    // ===== PERSONALISED CONTENT =====
    // =============================================================================

    /**
     * [SHARED] Updates the personalised pack heading based on name_on_pack value.
     * If name_on_pack exists, heading shows "{name}'s Personalised Pack".
     * Otherwise it shows "Your Personalised Pack".
     */
    function updatePersonalisedHeading() {
      try {
        // Get the heading element
        const headingElement = document.querySelector('.personalised-review-heading');
        if (!headingElement) {
          console.warn('[Review Manager] Could not find personalised-review-heading element');
          return;
        }

        // Get name_on_pack from localStorage
        const nameOnPack = localStorage.getItem('vitable_name_on_pack');

        // Update heading based on whether name_on_pack exists
        if (nameOnPack && nameOnPack.trim() !== '') {
          headingElement.textContent = `${nameOnPack}'s Personalised Pack`;
          if (window.personalised?.debugMode) {
            console.debug(`[Review Manager] Updated heading with name: ${nameOnPack}`);
          }
        } else {
          // Keep default text or reset if needed
          headingElement.textContent = 'Your Personalised Pack';
          if (window.personalised?.debugMode) {
            console.debug('[Review Manager] Using default heading (no name found)');
          }
        }
      } catch (error) {
        console.error('[Review Manager] Error updating personalised heading:', error);
        // Fail gracefully - heading will remain as is
      }
    }

    /**
     * [SHARED] Updates the quiz intro element based on when the user last took the quiz.
     * - If no quiz date: Show normal "Need some guidance?" message
     * - If recent quiz (< 48 hours): Hide the element
     * - If older quiz (> 48 hours): Show "Goals changed?" message
     */
    function updateReviewIntroQuiz() {
      try {
        // Get the quiz intro element
        const quizIntroElement = document.querySelector('.personalised-review-intro-quiz');
        if (!quizIntroElement) {
          console.warn('[Review Manager] Could not find personalised-review-intro-quiz element');
          return;
        }

        // Get quiz date from localStorage
        const latestQuizDate = localStorage.getItem('vitable_latest_quiz_date');

        // If no quiz date exists, keep the default view
        if (!latestQuizDate) {
          if (window.personalised?.debugMode) {
            console.debug('[Review Manager] No quiz date found, showing default quiz intro');
          }
          return;
        }

        // Parse the timestamp and calculate hours since quiz
        try {
          let quizTimestamp;

          // Check if the date is an ISO string (contains T, -, or :)
          if (typeof latestQuizDate === 'string' &&
              (latestQuizDate.includes('T') || latestQuizDate.includes('-') || latestQuizDate.includes(':'))) {
            // Parse ISO date string to timestamp
            quizTimestamp = new Date(latestQuizDate).getTime();
            if (window.personalised?.debugMode) {
              console.debug(`[Review Manager] Parsed ISO date string: ${latestQuizDate} to timestamp: ${quizTimestamp}`);
            }
          } else {
            // Try to parse as numeric timestamp
            quizTimestamp = parseInt(latestQuizDate, 10);
          }

          // Check if we have a valid timestamp
          if (isNaN(quizTimestamp)) {
            console.warn(`[Review Manager] Invalid quiz date format: ${latestQuizDate}`);
            return;
          }

          const currentTime = Date.now();
          const hoursSinceQuiz = (currentTime - quizTimestamp) / (1000 * 60 * 60);

          if (window.personalised?.debugMode) {
            console.debug(`[Review Manager] Quiz timestamp: ${quizTimestamp}, Current time: ${currentTime}, Hours since quiz: ${hoursSinceQuiz.toFixed(1)}`);
          }

          // Any quiz date = show retake message (no 48h distinction)
          quizIntroElement.innerHTML = 'Goals changed? <a href="/pages/quiz" data-no-quiz-autofix>Retake the expert quiz</a>';
          if (window.personalised?.debugMode) {
            console.debug(`[Review Manager] Quiz taken ${hoursSinceQuiz.toFixed(1)} hours ago, showing "Goals changed?" message`);
          }
        } catch (parseError) {
          console.error('[Review Manager] Error parsing quiz date timestamp:', parseError, latestQuizDate);
          // Fail gracefully - leave element as is
        }
      } catch (error) {
        console.error('[Review Manager] Error updating quiz intro element:', error);
        // Fail gracefully - element will remain as is
      }
    }

    // Public API
    return {
      renderBundleItems,
      surfaceSubscriptionStateSignal,
      updateUIForMode,
      updatePersonalisedHeading,
      updateReviewIntroQuiz
    };
  })();

  window.personalised.reviewRenderer = reviewRenderer;
})();
